import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";

export const MAX_FAVORITES = 3;

export function useFavorites() {
  const { user } = useAuth();
  const { prefs, loading, updatePreferences, refetchPreferences } = usePreferences();
  const favorites = prefs.favorite_chips;

  const toggleFavorite = useCallback(
    async (label: string): Promise<boolean> => {
      if (!user) return false;
      const isFav = favorites.includes(label);

      if (!isFav && favorites.length >= MAX_FAVORITES) {
        toast("You've got 3 favorites already. Unstar one to add this.");
        return false;
      }

      const next = isFav
        ? favorites.filter((l) => l !== label)
        : [...favorites, label];

      // .select() + a rows check, not just `if (error)`: an RLS-filtered upsert
      // matches zero rows and PostgREST answers 200 with error === null, so the
      // toast below could never fire for a refused write and the star stayed
      // lit until the next reload (house rule 16).
      //
      // The try/catch below wraps ONLY the raw request — supabase-js RESOLVES
      // with { error } for an API-level refusal, but the underlying fetch can
      // still REJECT for a genuine network failure (offline, aborted), and a
      // caught rejection needs the same failure path as a returned error
      // (Copilot, PR #876). `updatePreferences` deliberately stays OUTSIDE
      // this try: it resolves its own error rather than throwing, so wrapping
      // it here would be the decorative catch #545 already swept for.
      let failed = false;
      let errMessage: string | undefined;
      try {
        const { data, error } = await supabase
          .from("user_preferences")
          .upsert(
            { user_id: user.id, favorite_chips: next },
            { onConflict: "user_id" }
          )
          .select("user_id");
        failed = !!error || !data?.length;
        errMessage = error?.message;
      } catch (err) {
        failed = true;
        errMessage = err instanceof Error ? err.message : String(err);
      }

      if (failed) {
        console.warn("[Favorites] toggle failed", errMessage);
        toast.error("Couldn't save that. Try again?");
        return false;
      }
      // Sync context state (write already done above).
      await updatePreferences({ favorite_chips: next }, { write: false });
      return true;
    },
    [favorites, user, updatePreferences]
  );

  // Issue #822. The chip used to leave the screen whether or not the write
  // landed: the upsert's error was never read, and `{ write: false }` meant
  // nothing downstream re-checked it either. She saw the chip go, and saw it
  // come back on the next load — which reads as Kin being unreliable, not as
  // an error.
  //
  // No toast here on purpose. She didn't ask for a confirmation, she asked for
  // the chip to be gone; a failed optimistic write should UNDO itself rather
  // than announce itself.
  const removeFavorite = useCallback(
    async (label: string) => {
      if (!user) return;
      if (!favorites.includes(label)) return;
      // Snapshot the ONE field this write touches, into a plain local, BEFORE
      // the optimistic update. Deliberately not a ref synced in a useEffect —
      // that can hand back the NEW value and make the rollback a silent no-op
      // (#438 / #580).
      const previous = favorites;
      const next = favorites.filter((l) => l !== label);

      // Optimistic: the chip goes now, so the tap feels instant.
      await updatePreferences({ favorite_chips: next }, { write: false });

      // The try/catch wraps ONLY the raw request, same reasoning as
      // toggleFavorite above: supabase-js resolves with { error } for an
      // API-level refusal but can still reject outright for a network
      // failure, and without catching that the rollback below never ran
      // (Copilot, PR #876). `updatePreferences` stays outside the try —
      // it resolves its own error rather than throwing, so wrapping it here
      // would be the decorative catch #545 already swept for.
      let failed = false;
      let errMessage: string | undefined;
      try {
        const { data, error } = await supabase
          .from("user_preferences")
          .upsert(
            { user_id: user.id, favorite_chips: next },
            { onConflict: "user_id" }
          )
          .select("user_id");
        failed = !!error || !data?.length;
        errMessage = error?.message;
      } catch (err) {
        failed = true;
        errMessage = err instanceof Error ? err.message : String(err);
      }

      if (failed) {
        console.warn("[Favorites] remove failed", errMessage);
        // Field-scoped: favorite_chips only, so anything else that changed
        // while the write was in flight survives.
        await updatePreferences({ favorite_chips: previous }, { write: false });
      }
    },
    [favorites, user, updatePreferences]
  );

  return { favorites, loading, toggleFavorite, removeFavorite, refetch: refetchPreferences };
}
